import {
  confirmMethod,
  confirmOpening,
  deleteAnomaly,
  getStoreCameraConfigOptions,
  getStoreCameraListOptions,
  openDoor,
  useAnomalies,
  useBackOfficeBoutiques,
  useCheckinKiosks,
  useCheckoutKiosks,
  useAllPendingOpeningEvents,
  useLiveAiArticles,
  useLiveAnomalies,
  usePendingOpeningEvents,
} from '@gozoki/api'
import {
  RECEPTION_OPENING_EVENT_TYPES,
  type Kiosk as KioskType,
  type UniqueArticle,
} from '@gozoki/api-types'
import { DEFAULT_STORE_ID } from '@gozoki/tools'
import Box from '@mui/material/Box'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { LockOpen } from 'lucide-react'
import { useCallback } from 'react'
import { z } from 'zod/v3'
import {
  CAMERA_CONFIG_LEGACY_RIGHTS,
  CAMERA_STREAM_READ,
  CAMERAS_PAGE_READ,
  KIOSK_OPEN_DOOR,
} from '~/components/routes/cameras/permissions'
import { AuthorizedButtonLink, resolveMissingPermissions, withRoutePermissions } from '~/utils/rbac'
import '../../../assets/styles/CameraFont.module.css'
import InterventionButtons from '../../../components/buttons/Intervention.tsx'
import { AuthorizedButton } from '../../../components/communication/AuthorizedButton'
import { useToast } from '../../../components/communication/Toast.tsx'
import Page from '../../../components/Page.tsx'
import { StoreCameraViewer } from '../../../components/routes/camera-config/store-camera-viewer'
import AnomalyCards from '../../../components/routes/cameras/AnomalyCards.tsx'
import { getOpeningEventCard } from '../../../components/routes/cameras/openingEventCards.config.tsx'
import { StoresPicker } from '../../../utils/dashboard/storesPicker.tsx'
import { useOpeningEventUpdatedNotifications } from '../../../utils/websockets/useOpeningEventUpdatedNotifications.ts'
import { useSocketReconnect } from '../../../utils/websockets/useSocketReconnect.ts'

export const Route = createFileRoute('/_authenticated/cameras/')({
  staticData: {
    permission: CAMERAS_PAGE_READ,
  },
  // L'entrée de menu « Caméras » pointe ici, et la configuration caméra n'est atteignable que
  // depuis la barre d'outils de cet écran : un rôle qui n'a que `read cameras.cameras.config` est
  // redirigé vers elle au lieu de recevoir « Accès refusé » (BLOQ-03).
  beforeLoad: ({ context }) => {
    if (resolveMissingPermissions(context.ability, CAMERA_STREAM_READ).length === 0) return
    throw redirect({ to: '/cameras/config' })
  },
  component: CamerasPages,
  validateSearch: z.object({
    storeId: z.number().int().positive().optional(),
    focusedCameraId: z.number().int().positive().optional(),
  }),
})
withRoutePermissions(Route)

function CamerasPages() {
  const navigate = Route.useNavigate()
  const { storeId: storeIdSearch, focusedCameraId } = Route.useSearch()
  const storeId = storeIdSearch ?? DEFAULT_STORE_ID

  const [Toast, showToast] = useToast()

  const { data: boutiques } = useBackOfficeBoutiques()

  const currentStore = boutiques?.find((b) => b.id === storeId)

  const cameraConfigQuery = useQuery({
    ...getStoreCameraConfigOptions(storeId),
  })
  const cameraListQuery = useQuery({
    ...getStoreCameraListOptions(storeId),
  })

  const isJvcCamera = currentStore?.cameraType === 'JVC'
  const { data: checkinKiosks } = useCheckinKiosks(storeId)
  const { data: checkoutKiosks } = useCheckoutKiosks(storeId)

  const checkinKioskId = checkinKiosks?.length ? checkinKiosks[0].id : null

  const { data: anomalies } = useAnomalies(storeId)
  useLiveAnomalies(storeId)
  useLiveAiArticles(storeId)

  const { data: pendingOpeningEvents, refetch: refetchPendingOpeningEvents } =
    usePendingOpeningEvents(storeId)

  const { data: allPendingOpeningEvents, refetch: refetchAllPendingOpeningEvents } =
    useAllPendingOpeningEvents()

  const otherStoresPendingEvents = (allPendingOpeningEvents ?? []).filter(
    (event) => event.store !== null && event.store.id !== storeId,
  )

  const refetchOpeningEvents = useCallback(() => {
    void refetchPendingOpeningEvents()
    void refetchAllPendingOpeningEvents()
  }, [refetchPendingOpeningEvents, refetchAllPendingOpeningEvents])

  const { mutate: mutateConfirmMethod, isPending: isConfirmMethodPending } = useMutation({
    mutationFn: confirmMethod,
    onError: () => showToast({ severity: 'error', message: 'Erreur lors de la confirmation' }),
    onSettled: () => refetchOpeningEvents(),
  })

  const { mutate: mutateConfirmOpening, isPending: isConfirmOpeningPending } = useMutation({
    mutationFn: confirmOpening,
    onError: () =>
      showToast({ severity: 'error', message: "Erreur lors de la confirmation d'ouverture" }),
    onSettled: () => refetchOpeningEvents(),
  })

  const { mutate: mutateOpenDoor } = useMutation({
    mutationFn: (kioskId: number) => openDoor(kioskId, true),
    onSuccess: () => showToast({ severity: 'success', message: 'Ouverture de la porte demandée' }),
    onError: () =>
      showToast({ severity: 'error', message: "Erreur lors de l'ouverture de la porte" }),
  })

  useOpeningEventUpdatedNotifications(
    useCallback(
      (data) => {
        if (data.storeId === storeId) void refetchPendingOpeningEvents()
        void refetchAllPendingOpeningEvents()
      },
      [storeId, refetchPendingOpeningEvents, refetchAllPendingOpeningEvents],
    ),
  )

  useSocketReconnect(
    useCallback(() => {
      refetchOpeningEvents()
    }, [refetchOpeningEvents]),
  )

  const { mutate: mutateDeleteAnomaly } = useMutation({
    mutationFn: ({ sId, aId }: { sId: number; aId: string }) => deleteAnomaly(sId, aId),
    onError: () => {
      showToast({
        message: "Une erreur est survenue lors de la suppression de l'anomalie",
        severity: 'error',
      })
    },
  })

  const handleDeleteAnomaly = (anomalyId: string) => {
    mutateDeleteAnomaly({ aId: anomalyId, sId: storeId })
  }

  const handleChangeStore = (sId: number) => {
    // StoresPicker calls this once on mount with the current store id to sync its own
    // state, even when nothing changed — guard against that no-op wiping focusedCameraId
    // from the URL via the literal `search` below.
    if (sId === storeId) return
    navigate({ to: '/cameras', search: { storeId: sId } })
  }

  const handleCameraSelect = (selectedCameraId: number) =>
    navigate({
      to: '/cameras',
      search: { storeId, focusedCameraId: selectedCameraId },
    })

  const handleOpenDoor = () => {
    if (checkinKioskId === null) return

    const pendingEvent = (pendingOpeningEvents ?? []).find(
      (event) => event.kioskId === checkinKioskId,
    )
    const cpiId = checkinKiosks?.find((k) => k.id === checkinKioskId)?.cpiId

    if (pendingEvent?.method && cpiId) {
      mutateConfirmOpening(
        {
          kioskCpiId: cpiId,
          data: { method: pendingEvent.method, granted: true, openingEventId: pendingEvent.id },
        },
        {
          onSuccess: () => showToast({ severity: 'success', message: 'Ouverture confirmée' }),
        },
      )
      return
    }

    mutateOpenDoor(checkinKioskId)
  }

  return (
    <Page>
      <div style={{ marginLeft: '25px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: '20px',
            // MarginTop: '10px',
            height: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              height: '100%',
              justifyContent: 'center',
              // Flex: 3,
            }}
          >
            <div
              style={{
                color: 'darkgreen',
                fontFamily: 'BlockBerthold_regular',
                fontSize: '40px',
                fontWeight: '400',
                // Display: 'flex',
              }}
            >
              Caméras
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <InterventionButtons
                  checkType="CHECK-IN"
                  kioskId={checkinKioskId}
                  storeId={storeId}
                />
              </div>
              <div style={{ flex: 1 }}>
                {checkinKioskId && (
                  <div style={{ marginTop: '55px' }}>
                    <AuthorizedButton
                      permission={KIOSK_OPEN_DOOR}
                      legacyRights={CAMERA_CONFIG_LEGACY_RIGHTS}
                      variant="contained"
                      sx={{
                        '&:hover': { backgroundColor: 'darkorange' },
                        backgroundColor: 'orange',
                        width: '100%',
                      }}
                      onClick={handleOpenDoor}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        gap="8px"
                        style={{
                          color: '#3E2500',
                          font: 'Avenir Next',
                        }}
                      >
                        <span
                          style={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: '8px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <LockOpen style={{ height: 24, width: 24 }} />
                          Ouvrir la porte
                        </span>
                      </Box>
                    </AuthorizedButton>
                  </div>
                )}
              </div>
            </div>
            {cameraConfigQuery.isPending ? (
              <div
                className="animate-pulse rounded-lg bg-muted/40"
                style={{
                  aspectRatio: '1 / 1',
                  maxHeight: '80vh',
                  maxWidth: '80vh',
                  width: '100%',
                }}
              />
            ) : (
              <StoreCameraViewer
                cameraListError={cameraListQuery.error}
                cameraListPending={cameraListQuery.isPending}
                cameras={cameraListQuery.data ?? []}
                focusedCameraId={focusedCameraId}
                onCameraSelect={handleCameraSelect}
                previewStreamSuffix={isJvcCamera ? '_snapshot' : undefined}
                runtime={cameraConfigQuery.data?.runtime ?? null}
                storeId={storeId}
              />
            )}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              justifyContent: 'center',
            }}
          >
            <Toast />
            <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }} py={2}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: 500 }}>
                <AuthorizedButtonLink
                  variant="outlined"
                  to="/cameras/config"
                  search={{ storeId }}
                  legacyRights={CAMERA_CONFIG_LEGACY_RIGHTS}
                >
                  Configurer les caméras de cette boutique
                </AuthorizedButtonLink>
                <StoresPicker
                  onlyBoutiques
                  defaultStore={storeId}
                  setUniqueStore={handleChangeStore}
                  usedStores={boutiques?.map((s) => s.id)}
                />
              </Box>
            </Box>
            {checkoutKiosks?.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {checkoutKiosks.map((kiosk) => (
                  <KioskPanel key={kiosk.id} kiosk={kiosk} storeId={storeId} />
                ))}
              </div>
            ) : null}

            {pendingOpeningEvents && pendingOpeningEvents.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div
                  style={{
                    fontFamily: 'BlockBerthold_regular',
                    fontSize: '32px',
                    fontWeight: 400,
                    lineHeight: '40px',
                    textAlign: 'left',
                  }}
                >
                  OUVERTURE
                </div>
                {pendingOpeningEvents.map((event) => {
                  const kiosk = checkinKiosks?.find((k) => k.id === event.kioskId)
                  const Card = getOpeningEventCard(event)
                  const method = event.method
                  if (!Card || !method) return null
                  const isVerified = event.type === RECEPTION_OPENING_EVENT_TYPES.METHOD_VERIFIED
                  return (
                    <Card
                      key={event.id}
                      event={event}
                      kiosk={
                        kiosk
                          ? {
                              id: kiosk.id,
                              label: kiosk.label,
                              cpiId: kiosk.cpiId,
                              storeId,
                            }
                          : undefined
                      }
                      store={
                        currentStore
                          ? { id: currentStore.id, label: currentStore.label }
                          : undefined
                      }
                      isPending={isVerified ? isConfirmOpeningPending : isConfirmMethodPending}
                      decided={event.openingGranted != null}
                      onDecision={(kioskCpiId, granted, openingEventId) => {
                        if (isVerified)
                          mutateConfirmOpening({
                            kioskCpiId,
                            data: { method, granted, openingEventId },
                          })
                        else
                          mutateConfirmMethod({
                            kioskCpiId,
                            data: { method, granted, openingEventId },
                          })
                      }}
                    />
                  )
                })}
              </div>
            )}
            {otherStoresPendingEvents.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div
                  style={{
                    fontFamily: 'BlockBerthold_regular',
                    fontSize: '32px',
                    fontWeight: 400,
                    lineHeight: '40px',
                    textAlign: 'left',
                  }}
                >
                  AUTRES BOUTIQUES
                </div>
                {otherStoresPendingEvents.map((event) => {
                  const Card = getOpeningEventCard(event)
                  const method = event.method
                  if (!Card || !method) return null
                  const isVerified = event.type === RECEPTION_OPENING_EVENT_TYPES.METHOD_VERIFIED
                  return (
                    <Card
                      key={event.id}
                      event={event}
                      kiosk={event.kiosk ?? undefined}
                      store={event.store ?? undefined}
                      isPending={isVerified ? isConfirmOpeningPending : isConfirmMethodPending}
                      decided={event.openingGranted != null}
                      onDecision={(kioskCpiId, granted, openingEventId) => {
                        if (isVerified)
                          mutateConfirmOpening({
                            kioskCpiId,
                            data: { method, granted, openingEventId },
                          })
                        else
                          mutateConfirmMethod({
                            kioskCpiId,
                            data: { method, granted, openingEventId },
                          })
                      }}
                    />
                  )
                })}
              </div>
            )}
            <div style={{ height: '80%', overflowY: 'auto' }}>
              <AnomalyCards anomalies={anomalies} onDelete={handleDeleteAnomaly} />
            </div>
            <div style={{ flexGrow: 1 }} />
          </div>
        </div>
      </div>
    </Page>
  )
}

interface KioskPanelProps {
  kiosk: KioskType
  storeId: number
}

const KioskPanel = ({ kiosk, storeId }: KioskPanelProps) => {
  const { cart } = kiosk

  return (
    <div
      style={{
        border: '1px solid #D9D9D9',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '16px',
      }}
    >
      <div
        style={{
          fontFamily: 'Avenir Next',
          fontSize: '18px',
          fontWeight: 600,
        }}
      >
        {kiosk.label}
      </div>

      <InterventionButtons checkType="" kioskId={kiosk.id} storeId={storeId} />

      {cart && cart.articles && cart.articles.length > 0 && (
        <div>
          <div
            style={{
              color: '#2E7D32',
              fontFamily: 'Avenir Next',
              fontSize: '16px',
              fontWeight: 600,
              marginBottom: '8px',
            }}
          >
            Panier en cours ({cart.articles.length} articles)
          </div>
          <ul
            style={{
              fontFamily: 'Avenir Next',
              fontSize: '14px',
              lineHeight: '1.4',
              margin: 0,
              maxHeight: '150px',
              overflowY: 'auto',
              paddingLeft: '16px',
            }}
          >
            {cart.articles.map((article: UniqueArticle) => (
              <li
                key={article.id}
                style={{
                  color: '#333',
                  marginBottom: '4px',
                }}
              >
                {article.product?.label ?? 'Article'}&nbsp;&nbsp;(
                {article.product.sellPrice}€)
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
